// EditableShape adapter for Plant objects from GardenObjectState
// Supports move + rotate only — no resize (plants are modeled to real-world scale)
// x,y = CENTER (converted from API on import)

import type { EditableShape } from '@/types/editor'
import type { GardenObject } from '@/types/garden'
import { gardenObjectState } from '@/state/GardenObjectState'
import { undoStack } from '../UndoStack'
import { debug } from '@/utils/debug'

export class PlantAdapter implements EditableShape {
  readonly sourceType = 'plant' as const
  private objectId: string

  private snapshot: {
    x: number; y: number; rotation: number
  } | null = null

  constructor(obj: GardenObject) {
    this.objectId = obj.id
  }

  get id(): string {
    return this.objectId
  }

  private getObject(): GardenObject {
    const obj = gardenObjectState.getObjectById(this.objectId)
    if (!obj) throw new Error(`PlantAdapter: object ${this.objectId} not found`)
    return obj
  }

  // --- Read geometry ---

  getCenter(): { x: number; y: number } {
    const o = this.getObject()
    return { x: o.x, y: o.y }
  }

  getSize(): { width: number; height: number } {
    const o = this.getObject()
    return { width: o.width, height: o.height }
  }

  getRotation(): number {
    return this.getObject().rotation
  }

  getShapeKind(): 'rectangle' | 'ellipse' | 'polygon' {
    return 'rectangle'
  }

  getPoints(): Array<{ x: number; y: number }> | null {
    return null
  }

  // --- Write geometry ---

  moveTo(x: number, y: number): void {
    gardenObjectState.updateObject(this.objectId, { x, y })
  }

  resize(_width: number, _height: number): void {
    // No-op: plants are not resizable
  }

  rotate(degrees: number): void {
    gardenObjectState.updateObject(this.objectId, { rotation: degrees })
  }

  setPoints(_points: Array<{ x: number; y: number }>): void {
    // No-op: plants don't have polygon points
  }

  // --- Capabilities ---

  isResizable(): boolean {
    return false
  }

  // --- Visual ---

  getAccentColor(): number {
    return 0x92CC09 // Plant green
  }

  // --- Undo integration ---

  beginEdit(): void {
    const o = this.getObject()
    this.snapshot = { x: o.x, y: o.y, rotation: o.rotation }
  }

  commit(): void {
    if (!this.snapshot) return

    const before = this.snapshot
    const o = this.getObject()
    const after = { x: o.x, y: o.y, rotation: o.rotation }

    if (before.x === after.x && before.y === after.y && before.rotation === after.rotation) {
      this.snapshot = null
      return
    }

    const id = this.objectId
    undoStack.push({
      description: `Edit plant ${id}`,
      undo: () => gardenObjectState.updateObject(id, before),
      redo: () => gardenObjectState.updateObject(id, after),
    })

    debug.log('PlantAdapter', `Committed edit for ${id}`)
    this.snapshot = null
  }

  cancel(): void {
    if (!this.snapshot) return
    gardenObjectState.updateObject(this.objectId, this.snapshot)
    debug.log('PlantAdapter', `Cancelled edit for ${this.objectId}`)
    this.snapshot = null
  }
}
